import { CommonModule, DatePipe } from '@angular/common'
import { Component, ViewEncapsulation } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { SubscriptionService } from 'src/app/service/subscription/subscription.service'
import { UserService } from 'src/app/service/user-info/user.service'
import { environment } from '../../../environments/environment'
import { DdcTextGradientComponent } from '../../shared/ddc/ddc-text-gradient/ddc-text-gradient.component'
import { DdcPricingPlanComponent } from '../ddc-pricing-plan/ddc-pricing-plan.component'

@Component({
  selector: 'app-ddc-billing',
  templateUrl: './ddc-billing.component.html',
  styleUrl: './ddc-billing.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    DdcPricingPlanComponent,
    AngularMaterialsModules,
    DdcTextGradientComponent,
  ],
  providers: [DatePipe],
})
export class DdcBillingComponent {
  selectedPlan: string = ''
  startDate: Date = new Date()
  endDate: Date
  agreedToTerms: boolean = false
  username: string = ''
  subscription_plan: string = ''
  subscription_plan_plus_price: string = '34.99'
  subscription_plan_pro_price: string = '89.99'

  get currentPrice(): string {
    return this.selectedPlan === 'Pro'
      ? this.subscription_plan_pro_price
      : this.subscription_plan_plus_price
  }

  get currentMonthlyPrice(): string {
    return this.selectedPlan === 'Pro' ? '7.5' : '2.9'
  }

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private subscriptionService: SubscriptionService,
    private userService: UserService,
  ) {
    this.setPlanFromUrl()
    this.startDate = new Date()
    this.endDate = new Date(this.startDate)
    this.endDate.setFullYear(this.startDate.getFullYear() + 1)
    this.userService.get_user_profile().subscribe((res: any) => {
      this.username = res?.username || ''
      this.subscription_plan = res?.subscription || ''
    })
  }

  ngAfterViewInit(): void {
    if (this.agreedToTerms) {
      this.loadPayPalScript().then(() => {
        this.renderPayPalButton()
      })
    }
  }

  ngOnChanges(): void {
    if (this.agreedToTerms) {
      this.loadPayPalScript().then(() => {
        this.renderPayPalButton()
      })
    }
  }

  ngDoCheck(): void {
    // Render PayPal button only when agreedToTerms becomes true
    if (this.agreedToTerms && !(<any>window).paypalRendered) {
      this.loadPayPalScript().then(() => {
        this.renderPayPalButton()
        ;(<any>window).paypalRendered = true
      })
    } else if (!this.agreedToTerms && (<any>window).paypalRendered) {
      const container = document.getElementById('paypal-button-container')
      if (container) container.innerHTML = ''
      ;(<any>window).paypalRendered = false
    }
  }

  setPlanFromUrl(): void {
    const type = this.route.snapshot.queryParamMap.get('type')
    if (type === 'pro') {
      this.selectedPlan = 'Pro'
    } else {
      this.selectedPlan = 'Plus'
    }
  }

  loadPayPalScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((<any>window).paypal) {
        resolve() // already loaded
        return
      }

      const script = document.createElement('script')
      script.src = `https://www.paypal.com/sdk/js?client-id=${environment.paypalDaydicSellerClientId}&currency=EUR`
      script.onload = (): void => resolve()
      script.onerror = (): void => reject('PayPal SDK could not be loaded.')
      document.body.appendChild(script)
    })
  }

  generateUniqueAlphaNumericId(): string {
    const date = new Date()
    // Format: YYMMDD
    const datePart = [
      String(date.getFullYear()).slice(-2),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('')
    // Generate 4 random uppercase letters
    const letters = Array.from({ length: 4 }, () =>
      String.fromCharCode(65 + Math.floor(Math.random() * 26)),
    ).join('')
    // Generate 4 random digits
    const digits = Math.floor(1000 + Math.random() * 9000)
    return `${datePart}${letters}${digits}`
  }

  renderPayPalButton(): void {
    if (!this.subscription_plan || this.subscription_plan === 'Plus') {
      return // Do not render PayPal button if the user already has a subscription
    }
    console.log(
      'Rendering PayPal button for plan:',
      this.selectedPlan,
      'with price:',
      this.currentPrice,
    )
    // @ts-ignore
    paypal
      .Buttons({
        createOrder: (data: any, actions: any) => {
          return actions.order.create({
            purchase_units: [
              {
                amount: {
                  value: this.currentPrice,
                  currency_code: 'EUR',
                  breakdown: {
                    item_total: {
                      value: this.currentPrice,
                      currency_code: 'EUR',
                    },
                  },
                },
                items: [
                  {
                    name: `Daydic '${this.selectedPlan}' yearly subscription plan - ${this.startDate.toLocaleDateString()} to ${this.endDate.toLocaleDateString()} (User: ${this.username})`,
                    unit_amount: {
                      value: this.currentPrice,
                      currency_code: 'EUR',
                    },
                    quantity: '1',
                    sku: this.generateUniqueAlphaNumericId(),
                  },
                ],
              },
            ],
          })
        },
        onApprove: (data: any, actions: any) => {
          return actions.order.capture().then((details: any) => {
            const dialogRef = this.dialog.open(PaymentStatusDialogComponent, {
              disableClose: true,
              data: {
                message: 'Please do not close this window. Your subscription is being updated...',
              },
            })
            this.subscriptionService
              .updateSubscription({
                type: this.selectedPlan,
                start: this.startDate.toLocaleDateString(),
                end: this.endDate.toLocaleDateString(),
                transaction_id: details.id,
              })
              .subscribe({
                next: data => {
                  dialogRef.componentInstance.data.message = `Everything is OK! Your subscription is updated to '${this.selectedPlan}'. Forwarding to home page...`
                  setTimeout(() => {
                    dialogRef.close()
                    this.router.navigate(['/'])
                  }, 2000)
                },
                error: error => {
                  dialogRef.componentInstance.data.message =
                    'An error occurred while updating your subscription.'
                  setTimeout(() => dialogRef.close(), 3000)
                },
              })
          })
        },
        onError: (err: any) => {
          console.error('PayPal Button Error:', err)
          alert('An error occurred with the PayPal transaction.')
        },
      })
      .render('#paypal-button-container')
  }
}

// Dialog component for payment status
import { Inject } from '@angular/core'
import { MAT_DIALOG_DATA } from '@angular/material/dialog'

@Component({
  selector: 'payment-status-dialog',
  template: `<div style="padding:2em;min-width:250px;text-align:center;">
    <h3>{{ data.message }}</h3>
  </div>`,
})
export class PaymentStatusDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: { message: string }) {}
}
